import {
  RELAY_MODE,
  RelayModeValue,
  RelayTime,
  RelaySettingsProps,
  RelayTimeLocal,
  RelayTimeUpdatePayload
} from "./RelaySettings.model";
import {
  ClockStation_SetRelayModeModel,
  GetClockStationRelayInfo_GetClockStationRelayInfoResponse,
  GetClockStationRelayStatus_GetClockStationRelayStatusResponse,
  UpdateClockStationRelayDescriptionModel,
} from "../../../api";
import {useEffect, useState} from "react";
import UpdateButton from "../../shared/UpdateButton/UpdateButton";
import {RelaySettingsApi} from "../../../services/RelaySettings.api";
import StatusControl from "../../shared/StatusControl/StatusControl";
import {Input, InputGroup, RadioTile, RadioTileGroup, toaster} from "rsuite";
import "./RelaySettings.scss";
import NotificationFactory from "../../../shared/Notifications";
import TimeInput from "../../shared/TimeInput/TimeInput";
import {RelaySettingsUtils} from "./RelaySettings.utils";
import {TfiSaveIcon} from "../../../models/Icons.model";

const RelaySettings = ({deviceId, dawnTime, duskTime, withMelody}: RelaySettingsProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [params, setParams] = useState<GetClockStationRelayStatus_GetClockStationRelayStatusResponse | null>(null);

  const [relayModes, setRelayModes] = useState<Record<number, RelayModeValue>>({
    0: RelayModeValue.Disabled,
    1: RelayModeValue.Disabled
  });

  const [relayDescriptions, setRelayDescriptions] = useState({
    channel1: "",
    channel2: ""
  });

  const [relay1Time, setRelay1Time] = useState<RelayTimeLocal>({
    onHour: 0,
    onMinute: 0,
    offHour: 0,
    offMinute: 0
  });

  const [relay2Time, setRelay2Time] = useState<RelayTimeLocal>({
    onHour: 0,
    onMinute: 0,
    offHour: 0,
    offMinute: 0
  });

  useEffect(() => {
    getRelayParams();
    getRelayTime().then();
  }, [deviceId]);

  const getRelayParams = () => {
    getRelayStatus().then();
    getRelayDescription().then();
  };

  const parseTime = (timeStr: string): {hours: number; minutes: number} => {
    const [h, m] = timeStr.split(":");
    return {
      hours: Number(h) || 0,
      minutes: Number(m) || 0
    };
  };

  const formatTime = (hours: number, minutes: number): string => {
    const hh = String(hours).padStart(2, "0");
    const mm = String(minutes).padStart(2, "0");
    return `${hh}:${mm}:00`;
  };

  const getRelayTime = async () => {
    setLoading(true);
    try {
      const response: RelayTime = await RelaySettingsApi.getRelayTime(deviceId);

      const relay0 = response.schedulers["0"];
      const relay1 = response.schedulers["1"];

      if (relay0) {
        const on0 = parseTime(relay0.onTime);
        const off0 = parseTime(relay0.offTime);
        setRelay1Time({
          onHour: on0.hours,
          onMinute: on0.minutes,
          offHour: off0.hours,
          offMinute: off0.minutes
        });
      }

      if (relay1) {
        const on1 = parseTime(relay1.onTime);
        const off1 = parseTime(relay1.offTime);
        setRelay2Time({
          onHour: on1.hours,
          onMinute: on1.minutes,
          offHour: off1.hours,
          offMinute: off1.minutes
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getRelayStatus = async () => {
    setLoading(true);
    try {
      const response: GetClockStationRelayStatus_GetClockStationRelayStatusResponse = await RelaySettingsApi.getRelayStatus(deviceId);
      setParams(response);

      setRelayModes({
        0: (response.relays?.["0"]?.mode as RelayModeValue | undefined) ?? RelayModeValue.Disabled,
        1: (response.relays?.["1"]?.mode as RelayModeValue | undefined) ?? RelayModeValue.Disabled
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getRelayDescription = async () => {
    setLoading(true);
    try {
      const response: GetClockStationRelayInfo_GetClockStationRelayInfoResponse = await RelaySettingsApi.getRelayDescription(deviceId);

      if (response.relays) {
        setRelayDescriptions({
          channel1: response.relays["0"]?.description || "",
          channel2: response.relays["1"]?.description || ""
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateRelayTime = async (relayIndex: number, timeData: RelayTimeLocal) => {
    setLoading(true);
    try {
      const payload: RelayTimeUpdatePayload = {
        relayNumber: relayIndex,
        onTime: formatTime(timeData.onHour, timeData.onMinute),
        offTime: formatTime(timeData.offHour, timeData.offMinute)
      };

      const ok = await RelaySettingsApi.updateRelayTime(deviceId, payload);
      if (!ok) {
        throw new Error("updateRelayTime failed");
      }

      toaster.push(NotificationFactory.success(), {
        placement: "topEnd",
        duration: 3000
      });

      await getRelayTime();
      getRelayParams();
    } catch (err) {
      console.error(err);
      toaster.push(
        NotificationFactory.error("Ошибка обновления времени"),
        {
          placement: "topEnd",
          duration: 3000
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const onModeChange = async (relayIndex: number, value: RelayModeValue) => {
    const newModes = {
      ...relayModes,
      [relayIndex]: value
    };

    setRelayModes(newModes);

    try {
      setLoading(true);
      const payload: ClockStation_SetRelayModeModel = {modes: newModes};
      const result = await RelaySettingsApi.updateRelayModes(deviceId, payload);

      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });
        await getRelayStatus();
      }
    } catch (err) {
      console.error("Ошибка обновления режима", err);
      toaster.push(
        NotificationFactory.error("Ошибка обновления режима"),
        {
          placement: "topEnd",
          duration: 3000
        }
      );
      await getRelayStatus();
    } finally {
      setLoading(false);
    }
  };

  const onDescriptionChange = (channel: number, value: string) => {
    const descriptionKey = `channel${channel + 1}`;
    setRelayDescriptions(prev => ({
      ...prev,
      [descriptionKey]: value
    }));
  };

  const updateRelayDescription = async (relayNumber: number) => {
    const key = relayNumber === 0 ? "channel1" : "channel2";
    const description = relayDescriptions[key as keyof typeof relayDescriptions] || "";

    try {
      setLoading(true);

      const payload: UpdateClockStationRelayDescriptionModel = {
        relayNumber,
        description
      };

      const result = await RelaySettingsApi.updateRelayDescription(deviceId, payload);

      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });
        await getRelayDescription();
      }
    } catch (err) {
      console.error("Ошибка при обновлении описания реле", relayNumber + 1, err);
      toaster.push(
        NotificationFactory.error("Ошибка при обновлении описания реле"),
        {
          placement: "topEnd",
          duration: 3000
        }
      );
    } finally {
      setLoading(false);
    }
  };

  const relay1LabelExtra = RelaySettingsUtils.formatTimeRange(relay1Time);
  const relay2LabelExtra = RelaySettingsUtils.formatTimeRange(relay2Time);
  const dawnLabel = RelaySettingsUtils.formatAstronomicTime(dawnTime);
  const duskLabel = RelaySettingsUtils.formatAstronomicTime(duskTime);
  const astronomicLabelExtra = dawnLabel && duskLabel ? ` (${dawnLabel}–${duskLabel})` : "";

  const filteredRelayModesEntries = Object.entries(RELAY_MODE).filter(([key]) => {
    return !(!withMelody && key === RelayModeValue.Disabled);
  });

  return (
    <>
      {/* Настройки реле */}
      <div className="fieldset-wrapper mb-2">
        <div className="legend-title main-legend-title">Настройки реле</div>
        <section className="secondary-clock">
          <div className="row p-2">
            <div className="col-12 text-end">
              <UpdateButton loading={loading} onRefresh={getRelayStatus} />
            </div>
          </div>

          <div className="row p-2 pt-0">
            {/* 1 реле */}
            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mb-3">
              <div className="fieldset-wrapper control h-100 bg-white mt-0 pt-2">
                <div className="legend-title d-flex align-items-center" style={{height: 20}}>
                  <StatusControl status={params?.relays?.["0"]?.enabled ? "online" : "offline"} />
                  1 реле (1Р)
                </div>
                <div className="clock-wrapper w-100">
                  <RadioTileGroup
                    className="w-100"
                    aria-label="Relay 1 mode"
                    value={relayModes[0]}
                    onChange={value => onModeChange(0, value as RelayModeValue)}
                  >
                    {filteredRelayModesEntries.map(([key, text]) => {
                      let extra = "";

                      if (key === RelayModeValue.ConnectedToTimeRelay1) {
                        extra = ` (${relay1LabelExtra})`;
                      } else if (key === RelayModeValue.ConnectedToTimeRelay2) {
                        extra = ` (${relay2LabelExtra})`;
                      } else if (key === RelayModeValue.ConnectedToAstronomicalRelay && astronomicLabelExtra) {
                        extra = astronomicLabelExtra;
                      }

                      return (
                        <RadioTile
                          className="mb-0"
                          key={key}
                          value={key}
                          label={text}
                        >
                          <span className="radio-tile-text">
                            {text}
                            {extra}
                          </span>
                        </RadioTile>
                      );
                    })}
                  </RadioTileGroup>
                </div>
                <div className="mb-3">
                  <div className="col-md-12">
                    <InputGroup>
                      <InputGroup.Addon>Описание</InputGroup.Addon>
                      <Input
                        className="flex-grow-1"
                        placeholder="Введите описание для реле 1"
                        value={relayDescriptions.channel1 || ""}
                        onChange={value => onDescriptionChange(0, value)}
                      />
                      <InputGroup.Addon>
                        <TfiSaveIcon
                          style={{cursor: "pointer"}}
                          onClick={() => updateRelayDescription(0)}
                        />
                      </InputGroup.Addon>
                    </InputGroup>
                  </div>
                </div>
              </div>
            </div>

            {/* 2 реле */}
            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mb-3">
              <div className="fieldset-wrapper control h-100 bg-white mt-0 pt-2">
                <div className="legend-title d-flex align-items-center" style={{height: 20}}>
                  <StatusControl status={params?.relays?.["1"]?.enabled ? "online" : "offline"} />
                  2 реле (2Р)
                </div>
                <div className="clock-wrapper w-100">
                  <RadioTileGroup
                    disabled={loading}
                    className="w-100"
                    aria-label="Relay 2 mode"
                    value={relayModes[1]}
                    onChange={value => onModeChange(1, value as RelayModeValue)}
                  >
                    {filteredRelayModesEntries.map(([key, text]) => {
                      let extra = "";

                      if (key === RelayModeValue.ConnectedToTimeRelay1) {
                        extra = ` (${relay1LabelExtra})`;
                      } else if (key === RelayModeValue.ConnectedToTimeRelay2) {
                        extra = ` (${relay2LabelExtra})`;
                      } else if (key === RelayModeValue.ConnectedToAstronomicalRelay && astronomicLabelExtra) {
                        extra = astronomicLabelExtra;
                      }

                      return (
                        <RadioTile
                          className="mb-0"
                          key={key}
                          value={key}
                          label={text}
                        >
                          <span className="radio-tile-text">
                            {text}
                            {extra}
                          </span>
                        </RadioTile>
                      );
                    })}
                  </RadioTileGroup>
                </div>
                <div className="row mb-3">
                  <div className="col-md-12">
                    <InputGroup>
                      <InputGroup.Addon>Описание</InputGroup.Addon>
                      <Input
                        className="flex-grow-1"
                        placeholder="Введите описание для реле 2"
                        value={relayDescriptions.channel2 || ""}
                        onChange={value => onDescriptionChange(1, value)}
                      />
                      <InputGroup.Addon>
                        <TfiSaveIcon
                          style={{cursor: "pointer"}}
                          onClick={() => updateRelayDescription(1)}
                        />
                      </InputGroup.Addon>
                    </InputGroup>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Реле времени */}
      <div className="fieldset-wrapper mb-2">
        <div className="legend-title main-legend-title">Реле времени</div>
        <section className="secondary-clock">
          <div className="row p-2">
            <div className="col-12 text-end">
              <UpdateButton loading={loading} onRefresh={getRelayTime} />
            </div>
          </div>
          <div className="row p-2 pt-0">
            {/* 1 канал */}
            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mb-3">
              <div className="fieldset-wrapper control bg-white mt-0 pt-2">
                <div className="legend-title">Реле времени 1</div>
                <div className="row mt-3">
                  <div className="col-md-12 d-flex justify-content-center mb-3">
                    <InputGroup style={{width: 325}}>
                      <InputGroup.Addon style={{width: 155}}>Время включения</InputGroup.Addon>
                      <TimeInput
                        hours={relay1Time.onHour}
                        minutes={relay1Time.onMinute}
                        onTimeChanged={({hours, minutes}: {hours: number; minutes: number}) =>
                          setRelay1Time(prev => ({...prev, onHour: hours, onMinute: minutes}))
                        }
                      />
                      <InputGroup.Addon>
                        <TfiSaveIcon
                          style={{cursor: "pointer"}}
                          onClick={() => updateRelayTime(0, relay1Time)}
                        />
                      </InputGroup.Addon>
                    </InputGroup>
                  </div>
                  <div className="col-md-12 d-flex justify-content-center">
                    <InputGroup style={{width: 325}}>
                      <InputGroup.Addon style={{width: 155}}>Время выключения</InputGroup.Addon>
                      <TimeInput
                        hours={relay1Time.offHour}
                        minutes={relay1Time.offMinute}
                        onTimeChanged={({hours, minutes}: {hours: number; minutes: number}) =>
                          setRelay1Time(prev => ({...prev, offHour: hours, offMinute: minutes}))
                        }
                      />
                      <InputGroup.Addon>
                        <TfiSaveIcon
                          style={{cursor: "pointer"}}
                          onClick={() => updateRelayTime(0, relay1Time)}
                        />
                      </InputGroup.Addon>
                    </InputGroup>
                  </div>
                </div>
              </div>
            </div>

            {/* 2 канал */}
            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mb-3">
              <div className="fieldset-wrapper control bg-white mt-0 pt-2">
                <div className="legend-title">Реле времени 2</div>
                <div className="row mt-3">
                  <div className="col-md-12 d-flex justify-content-center mb-3">
                    <InputGroup style={{width: 325}}>
                      <InputGroup.Addon style={{width: 155}}>Время включения</InputGroup.Addon>
                      <TimeInput
                        hours={relay2Time.onHour}
                        minutes={relay2Time.onMinute}
                        onTimeChanged={({hours, minutes}: {hours: number; minutes: number}) =>
                          setRelay2Time(prev => ({...prev, onHour: hours, onMinute: minutes}))
                        }
                      />
                      <InputGroup.Addon>
                        <TfiSaveIcon
                          style={{cursor: "pointer"}}
                          onClick={() => updateRelayTime(1, relay2Time)}
                        />
                      </InputGroup.Addon>
                    </InputGroup>
                  </div>
                  <div className="col-md-12 d-flex justify-content-center mb-2">
                    <InputGroup style={{width: 325}}>
                      <InputGroup.Addon style={{width: 155}}>Время выключения</InputGroup.Addon>
                      <TimeInput
                        hours={relay2Time.offHour}
                        minutes={relay2Time.offMinute}
                        onTimeChanged={({hours, minutes}: {hours: number; minutes: number}) =>
                          setRelay2Time(prev => ({...prev, offHour: hours, offMinute: minutes}))
                        }
                      />
                      <InputGroup.Addon>
                        <TfiSaveIcon
                          style={{cursor: "pointer"}}
                          onClick={() => updateRelayTime(1, relay2Time)}
                        />
                      </InputGroup.Addon>
                    </InputGroup>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default RelaySettings;
